import { DateTime, Num, T, Var } from 'gt-react';
import type { LegPriceBreak } from '@erp/shared-types';
import { parseDateOnlyLocal } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { formatUnitCost } from './unitCostFormat';

/**
 * Where a buy line's unit cost came from, as one compact badge
 * (docs/procurement/forward-buying.md, Part 2, decision 17): "Contract 4046 ·
 * ends Sep 30" when a supplier cost agreement priced it, "Tier 2 at $6.0000"
 * when one of the supplier product row's price breaks did, and nothing at the
 * row's list cost, which is every line nothing better applies to.
 *
 * Reads the price provenance the engine froze onto the buy leg, so it names
 * the rung that priced the saved quantity, not a quantity being typed.
 */

const BADGE_CLASS =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium';

/** `surface` on the board and in the detail modal; `inverse` inside a tooltip. */
type CostSourceBadgeTone = 'surface' | 'inverse';

const TONE_CLASS: Record<CostSourceBadgeTone, string> = {
  surface: 'bg-gray-100 text-gray-700',
  inverse: 'bg-white/15 text-primary-foreground',
};

/** The agreement's last valid day, "Sep 30": the badge is too narrow for a year. */
const ENDS_ON_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
};

function ContractLabel({
  contractNumber,
  endsOn,
}: {
  contractNumber: number | null;
  endsOn: Date | null;
}) {
  // Provenance always records the contract number with the agreement; a blob
  // without one still says a contract priced the line.
  if (contractNumber === null) {
    return (
      <T context="replenishment cost-source badge: a supplier cost agreement priced this buy line; its contract number is unknown">
        Contract price
      </T>
    );
  }
  if (endsOn === null) {
    return (
      <T context="replenishment cost-source badge: the supplier contract number that priced this buy line; its end date is unknown">
        Contract <Var>{String(contractNumber)}</Var>
      </T>
    );
  }
  return (
    <T context="replenishment cost-source badge: the supplier contract number that priced this buy line, and the last day that contract is valid">
      Contract <Var>{String(contractNumber)}</Var> · ends{' '}
      <DateTime options={ENDS_ON_FORMAT}>{endsOn}</DateTime>
    </T>
  );
}

interface CostSourceBadgeProps {
  priceBreak: LegPriceBreak | null | undefined;
  tone?: CostSourceBadgeTone;
  className?: string;
}

export function CostSourceBadge({
  priceBreak,
  tone = 'surface',
  className,
}: CostSourceBadgeProps) {
  if (priceBreak == null) return null;
  const badgeClass = cn(BADGE_CLASS, TONE_CLASS[tone], className);

  if (priceBreak.costSource === 'agreement') {
    const endsOn =
      priceBreak.constraintEndsOn == null
        ? null
        : parseDateOnlyLocal(priceBreak.constraintEndsOn);
    return (
      <span className={badgeClass}>
        <ContractLabel
          contractNumber={priceBreak.contractNumber}
          endsOn={endsOn}
        />
      </span>
    );
  }

  const { tier, unitCost } = priceBreak;
  // List cost needs no badge, and a legacy leg can carry a partial blob: the
  // tier badge only reads with both numbers.
  if (
    priceBreak.costSource !== 'price_break' ||
    tier == null ||
    unitCost == null
  ) {
    return null;
  }
  return (
    <span className={badgeClass}>
      <T context="replenishment suggestion detail badge: the vendor price-break tier this buy leg reached and the unit cost there">
        Tier <Num>{tier}</Num> at <Var>{formatUnitCost(unitCost)}</Var>
      </T>
    </span>
  );
}
